if (!customElements.get('hero-avatars')) {
  class HeroAvatars extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.hero__avatars-track');
      if (!this.track) return;

      this.items = () => Array.from(this.track.querySelectorAll('.hero__avatar-item'));
      this.interval = parseInt(this.dataset.interval, 10) || 3000;
      this.maxVisible = parseInt(this.dataset.visible, 10) || 5;
      this.slideDuration = 450;
      this.isAnimating = false;

      const stepValue = getComputedStyle(this).getPropertyValue('--hero-avatar-step').trim();
      const sizeValue = getComputedStyle(this).getPropertyValue('--hero-avatar-size').trim();
      this.stepPx = this.cssLengthToPx(stepValue, 24);
      this.avatarSizePx = this.cssLengthToPx(sizeValue, 32);

      this.layoutItems(false);

      if (this.items().length < 2) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.timer = window.setInterval(() => this.slide(), this.interval);
    }

    disconnectedCallback() {
      if (this.timer) window.clearInterval(this.timer);
    }

    cssLengthToPx(value, fallback) {
      if (!value) return fallback;

      if (value.endsWith('rem')) {
        return parseFloat(value) * parseFloat(getComputedStyle(document.documentElement).fontSize);
      }

      if (value.endsWith('px')) {
        return parseFloat(value);
      }

      return parseFloat(value) || fallback;
    }

    getVisibleCount() {
      return Math.min(this.maxVisible, this.items().length);
    }

    hideItem(item) {
      item.style.opacity = '0';
      item.style.visibility = 'hidden';
      item.style.zIndex = '0';
    }

    showItem(item, left, zIndex) {
      item.style.left = `${left}px`;
      item.style.opacity = '1';
      item.style.visibility = 'visible';
      item.style.zIndex = `${zIndex}`;
    }

    layoutItems(animate) {
      const visibleCount = this.getVisibleCount();

      this.items().forEach((item, index) => {
        item.style.transition = animate ? this.transitionStyle : 'none';

        if (index < visibleCount) {
          this.showItem(item, index * this.stepPx, index + 1);
        } else {
          item.style.transition = 'none';
          item.style.left = `${(visibleCount - 1) * this.stepPx}px`;
          this.hideItem(item);
        }
      });
    }

    get transitionStyle() {
      return `left ${this.slideDuration}ms ease, opacity ${this.slideDuration}ms ease`;
    }

    slide() {
      if (this.isAnimating) return;
      this.isAnimating = true;

      this.classList.add('is-sliding');

      const items = this.items();
      const count = items.length;
      const visibleCount = this.getVisibleCount();
      const exiting = items[count - 1];
      const exitRight = this.avatarSizePx + (visibleCount - 1) * this.stepPx;
      const visibleRight = (visibleCount - 1) * this.stepPx;

      exiting.classList.add('hero__avatar-item--exiting');

      const entering = exiting.cloneNode(true);
      entering.classList.add('hero__avatar-item--entering');
      entering.setAttribute('aria-hidden', 'true');
      this.track.prepend(entering);

      const animatedItems = this.items();

      animatedItems.forEach((item, index) => {
        item.style.transition = 'none';

        if (index === 0) {
          item.style.left = `${-this.avatarSizePx}px`;
          item.style.opacity = '0';
          item.style.visibility = 'visible';
          item.style.zIndex = '1';
        } else if (index === count) {
          this.showItem(item, visibleRight, visibleCount);
        } else if (index >= visibleCount) {
          item.style.left = `${visibleRight}px`;
          this.hideItem(item);
        } else {
          this.showItem(item, (index - 1) * this.stepPx, index);
        }
      });

      this.track.offsetHeight;

      animatedItems.forEach((item, index) => {
        if (index === count) {
          const fadeDuration = this.slideDuration * 0.25;
          const fadeDelay = this.slideDuration * 0.15;
          item.style.transition = `left ${this.slideDuration}ms ease, opacity ${fadeDuration}ms ease ${fadeDelay}ms`;
        } else {
          item.style.transition = this.transitionStyle;
        }

        if (index === 0) {
          item.style.left = '0';
          item.style.opacity = '1';
          item.style.visibility = 'visible';
        } else if (index === count) {
          item.style.left = `${exitRight}px`;
          item.style.opacity = '0';
          item.style.visibility = 'visible';
        } else if (index >= visibleCount) {
          item.style.left = `${visibleRight}px`;
          this.hideItem(item);
        } else {
          this.showItem(item, index * this.stepPx, index);
        }
      });

      window.setTimeout(() => {
        entering.remove();
        exiting.classList.remove('hero__avatar-item--exiting');
        this.track.prepend(exiting);
        this.classList.remove('is-sliding');
        this.layoutItems(false);
        this.isAnimating = false;
      }, this.slideDuration);
    }
  }

  customElements.define('hero-avatars', HeroAvatars);
}
